// Play with a game controller (house module, 2026-11-02: Matt plays the hub
// games on a Steam Deck docked to the TV - "I want controller support where
// it makes sense"). Framework-free, the same file in every game that doesn't
// read a controller itself.
//
// The d-pad (or left stick) moves a highlight between the things on screen
// you can press - buttons, links, cards, anything with a pointer cursor - by
// where they sit, not by page order: right goes to the nearest thing to the
// right. A presses it. B is Escape (closes a dialog). The right stick
// scrolls. Left/right on a select or a range changes it.
//
// Until a controller is plugged in this does nothing at all - no listeners
// that matter, no styles applied - so the web and phone versions cannot be
// made worse by it. Games that read the controller themselves (Deck City,
// Couch Blocks, Blockout) don't load it.
//
// Pure parts (pickNext, padActions, createRepeater) are exported for tests.

const REPEAT_DELAY_MS = 300;
const REPEAT_RATE_MS = 110;
const DEADZONE = 0.5;

// Standard-mapping buttons.
const BUTTONS = { 0: 'a', 1: 'b', 2: 'x', 3: 'y', 4: 'lb', 5: 'rb', 8: 'view', 9: 'start', 12: 'up', 13: 'down', 14: 'left', 15: 'right' };
const DIRS = ['up', 'down', 'left', 'right'];
const PRESS = ['onClick', 'onPointerdown', 'onMousedown', 'onTouchstart', 'onPointerup', 'onMouseup'];

const CANDIDATES = 'button, a[href], select, input:not([type=hidden]), textarea, summary, [role=button], [role=tab], [role=option], [tabindex]:not([tabindex="-1"]), [data-pad]';

/** What one pad is asking for right now. */
export function padActions (pad) {
  const held = new Set();
  if (!pad) return held;
  pad.buttons.forEach((b, i) => { if (b && b.pressed && BUTTONS[i]) held.add(BUTTONS[i]); });
  const [lx = 0, ly = 0, , ry = 0] = pad.axes;
  if (lx < -DEADZONE) held.add('left');
  if (lx > DEADZONE) held.add('right');
  if (ly < -DEADZONE) held.add('up');
  if (ly > DEADZONE) held.add('down');
  if (ry < -DEADZONE) held.add('scrollUp');
  if (ry > DEADZONE) held.add('scrollDown');
  return held;
}

/** Held into fired: once on the press; directions and scrolling repeat while held. */
export function createRepeater (delay = REPEAT_DELAY_MS, rate = REPEAT_RATE_MS) {
  const since = new Map();
  return {
    tick (held, now) {
      const fired = [];
      for (const a of held) {
        const e = since.get(a);
        if (!e) { since.set(a, { at: now, fired: now }); fired.push(a); continue; }
        const repeats = DIRS.includes(a) || a === 'scrollUp' || a === 'scrollDown';
        if (repeats && now - e.at >= delay && now - e.fired >= rate) { e.fired = now; fired.push(a); }
      }
      for (const a of [...since.keys()]) if (!held.has(a)) since.delete(a);
      return fired;
    },
    reset () { since.clear(); },
  };
}

/**
 * The best next box in direction `dir` from box `from`, among `boxes`
 * ({ left, top, right, bottom }). It must lie that way at all; then the
 * nearest wins, with sideways offset counting double, so right means "the
 * thing on this row", not "something far down the screen that is a hair to
 * the right". Returns an index into `boxes`, or -1.
 */
export function pickNext (from, boxes, dir) {
  const cx = (from.left + from.right) / 2;
  const cy = (from.top + from.bottom) / 2;
  let best = -1; let bestScore = Infinity;
  boxes.forEach((b, i) => {
    const bx = (b.left + b.right) / 2; const by = (b.top + b.bottom) / 2;
    let main; let side;
    if (dir === 'right') { if (b.left < from.right - 2 && bx <= cx + 2) return; main = Math.max(0, b.left - from.right); side = Math.abs(by - cy); if (bx <= cx) return; }
    if (dir === 'left') { if (b.right > from.left + 2 && bx >= cx - 2) return; main = Math.max(0, from.left - b.right); side = Math.abs(by - cy); if (bx >= cx) return; }
    if (dir === 'down') { if (b.top < from.bottom - 2 && by <= cy + 2) return; main = Math.max(0, b.top - from.bottom); side = Math.abs(bx - cx); if (by <= cy) return; }
    if (dir === 'up') { if (b.bottom > from.top + 2 && by >= cy - 2) return; main = Math.max(0, from.top - b.bottom); side = Math.abs(bx - cx); if (by >= cy) return; }
    // Overlapping on the cross axis is the same row/column: barely any sideways cost.
    const overlap = dir === 'left' || dir === 'right'
      ? Math.min(b.bottom, from.bottom) - Math.max(b.top, from.top)
      : Math.min(b.right, from.right) - Math.max(b.left, from.left);
    const score = main + (overlap > 0 ? side * 0.3 : side * 2 + 40);
    if (score < bestScore) { bestScore = score; best = i; }
  });
  return best;
}

// ---- In the page ----

const visible = (el) => {
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return null;
  if (r.bottom < 0 || r.right < 0 || r.top > innerHeight || r.left > innerWidth) return null;
  const s = getComputedStyle(el);
  if (s.visibility === 'hidden' || s.pointerEvents === 'none' || Number(s.opacity) === 0) return null;
  return r;
};

/** Is `el` the thing actually under its own middle (not behind a dialog or another layer)? */
const onTop = (el, r) => {
  const x = Math.min(Math.max(r.left + r.width / 2, 0), innerWidth - 1);
  const y = Math.min(Math.max(r.top + r.height / 2, 0), innerHeight - 1);
  const hit = document.elementFromPoint(x, y);
  return !!hit && (hit === el || el.contains(hit) || hit.contains(el));
};

/** Everything pressable on screen right now, outermost only (a card, not the span inside it). */
function candidates () {
  const found = new Set(document.querySelectorAll(CANDIDATES));
  // Clickable things that aren't buttons: anything with a pointer cursor, and
  // anything Vue has put a press handler on (it keeps them on the element as
  // _vei), which catches a map cell that only listens for pointerdown.
  for (const el of document.body.querySelectorAll('div, li, span, img, svg, td, label, figure, article, section, g, rect, circle, path, canvas')) {
    if (found.has(el)) continue;
    const vue = el._vei;
    if ((vue && PRESS.some((k) => vue[k])) || getComputedStyle(el).cursor === 'pointer') found.add(el);
  }
  const out = [];
  for (const el of found) {
    if (el.disabled || el.closest('[inert], [aria-hidden="true"], .pad-nav-ignore')) continue;
    if (el.id === 'pad-nav-hint') continue;
    const r = visible(el);
    if (!r || !onTop(el, r)) continue;
    out.push({ el, r });
  }
  // Something inside a real button or link is that button, not a target of its own.
  return out.filter(({ el }) => !el.matches(CANDIDATES) ? !out.some((o) => o.el !== el && o.el.contains(el) && o.el.matches(CANDIDATES)) : true);
}

let current = null; // the highlighted element
let lastBox = null; // where it was, for when the page re-renders under us

function mark (el) {
  if (current && current !== el) current.classList.remove('pad-focus');
  current = el;
  if (!el) return;
  el.classList.add('pad-focus');
  lastBox = el.getBoundingClientRect();
  if (el.matches('button, a[href], select, input, textarea, [tabindex]')) el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/** The highlight still on screen, or the nearest thing to where it was. */
function ensureCurrent (list) {
  if (current && current.isConnected && list.some((c) => c.el === current)) return current;
  if (!list.length) { mark(null); return null; }
  let pick = list[0];
  if (lastBox) {
    const cx = (lastBox.left + lastBox.right) / 2; const cy = (lastBox.top + lastBox.bottom) / 2;
    let best = Infinity;
    for (const c of list) {
      const d = Math.hypot((c.r.left + c.r.right) / 2 - cx, (c.r.top + c.r.bottom) / 2 - cy);
      if (d < best) { best = d; pick = c; }
    }
  } else {
    // First press: a primary-looking button if there is one, else the button nearest the middle of the screen.
    const mid = (c) => Math.hypot((c.r.left + c.r.right) / 2 - innerWidth / 2, (c.r.top + c.r.bottom) / 2 - innerHeight / 2);
    const real = list.filter((c) => !c.el.closest('.bug-trigger, .build-stamp, .hub-link'));
    pick = list.find((c) => c.el.matches('.primary, [data-pad-first], button[type=submit]')) ?? (real.length ? real : list).slice().sort((a, b) => mid(a) - mid(b))[0];
  }
  mark(pick.el);
  fresh = true;
  return pick.el;
}
let fresh = false; // ensureCurrent had to pick something new
let lastShown = false; // the very first highlight of this visit has been shown

function press (el) {
  if (el.matches('input:not([type=checkbox]):not([type=radio]):not([type=range]), textarea')) { el.focus(); return; }
  const r = el.getBoundingClientRect();
  const at = { bubbles: true, cancelable: true, clientX: r.left + r.width / 2, clientY: r.top + r.height / 2, button: 0, pointerId: 1, pointerType: 'mouse', isPrimary: true };
  // Some games act on pointerdown (no tap delay), others on click; send the whole gesture.
  el.dispatchEvent(new PointerEvent('pointerdown', at));
  el.dispatchEvent(new MouseEvent('mousedown', at));
  el.dispatchEvent(new PointerEvent('pointerup', at));
  el.dispatchEvent(new MouseEvent('mouseup', at));
  el.click();
}

function nudge (el, step) {
  if (el instanceof HTMLSelectElement) {
    const i = el.selectedIndex + step;
    if (i < 0 || i >= el.options.length) return true;
    el.selectedIndex = i;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  if (el instanceof HTMLInputElement && el.type === 'range') {
    if (step > 0) el.stepUp(); else el.stepDown();
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  return false;
}

/** The box that scrolls: the scrollable one round the highlight, else the biggest scrollable thing, else the page. */
function scroller () {
  const scrolls = (el) => { const s = getComputedStyle(el); return /(auto|scroll)/.test(s.overflowY) && el.scrollHeight > el.clientHeight + 4; };
  for (let box = current?.parentElement; box && box !== document.body; box = box.parentElement) if (scrolls(box)) return box;
  let best = null; let area = 0;
  for (const el of document.body.querySelectorAll('*')) {
    if (!scrolls(el)) continue;
    const r = el.getBoundingClientRect(); const a = r.width * r.height;
    if (a > area) { area = a; best = el; }
  }
  const page = document.scrollingElement;
  return best && (page.scrollHeight <= page.clientHeight + 4 || area > innerWidth * innerHeight * 0.4) ? best : page;
}
const canScroll = (box, dy) => (dy > 0 ? box.scrollTop + box.clientHeight < box.scrollHeight - 2 : box.scrollTop > 0);

/** Pinned to the screen (the bug button, the build stamp): it doesn't move when the page scrolls. */
const pinned = (el) => {
  for (let e = el; e && e !== document.body; e = e.parentElement) {
    const p = getComputedStyle(e).position;
    if (p === 'fixed' || p === 'sticky') return true;
  }
  return false;
};

const GLYPHS = {
  xbox: { a: 'A', b: 'B' },
  playstation: { a: '✕', b: '○' },
  switch: { a: 'B', b: 'A' },
};
export function padKind (id = '') {
  const s = id.toLowerCase();
  if (/xbox|xinput|045e|microsoft/.test(s)) return 'xbox';
  if (/pro controller|joy-con|nintendo|057e/.test(s)) return 'switch';
  if (/dualsense|dualshock|playstation|wireless controller|054c/.test(s)) return 'playstation';
  return 'xbox';
}

function hint (kind) {
  let el = document.getElementById('pad-nav-hint');
  if (!kind) { el?.remove(); return; }
  if (!el) { el = document.createElement('div'); el.id = 'pad-nav-hint'; document.body.appendChild(el); }
  const g = GLYPHS[kind];
  el.innerHTML = `<b>✥</b> move <b>${g.a}</b> select <b>${g.b}</b> back`;
  el.classList.remove('pad-nav-hint--fade');
  clearTimeout(hint.timer);
  hint.timer = setTimeout(() => el.classList.add('pad-nav-hint--fade'), 6000);
}

const CSS = `
.pad-nav .pad-focus { outline: 3px solid #ffd23f !important; outline-offset: 3px !important; box-shadow: 0 0 0 6px rgba(255, 210, 63, 0.28) !important; border-radius: 6px; }
#pad-nav-hint { position: fixed; left: 50%; bottom: 14px; transform: translateX(-50%); z-index: 2147483000; padding: 7px 14px; border-radius: 999px; background: rgba(10, 12, 18, 0.82); color: #f4f1ea; font: 600 14px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; pointer-events: none; transition: opacity 0.6s; }
#pad-nav-hint b { display: inline-block; min-width: 1.4em; margin: 0 4px 0 10px; padding: 1px 6px; border-radius: 6px; background: #ffd23f; color: #111; text-align: center; }
#pad-nav-hint b:first-child { margin-left: 0; }
#pad-nav-hint.pad-nav-hint--fade { opacity: 0; }
.deck #pad-nav-hint { top: 10px; bottom: auto; }
`;

function act (action) {
  if (action === 'scrollUp' || action === 'scrollDown') { scroller().scrollBy({ top: action === 'scrollUp' ? -160 : 160, behavior: 'smooth' }); return; }
  if (action === 'b') {
    // A close button on whatever is on top (a dialog's ✕), else Escape.
    const close = candidates().find(({ el }) => /close|dismiss/i.test(el.getAttribute('aria-label') || el.title || '') || /^(✕|×|✖|close|cancel|done|got it)$/i.test((el.innerText || '').trim()));
    if (close) { press(close.el); return; }
    const target = document.activeElement && document.activeElement !== document.body ? document.activeElement : document;
    for (const type of ['keydown', 'keyup']) target.dispatchEvent(new KeyboardEvent(type, { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true }));
    return;
  }
  const list = candidates();
  fresh = false;
  const el = ensureCurrent(list);
  if (!el) return;
  if (fresh && DIRS.includes(action) && !lastShown) { lastShown = true; return; }
  if (action === 'a') { press(el); return; }
  if (DIRS.includes(action)) {
    if ((action === 'left' || action === 'right') && nudge(el, action === 'left' ? -1 : 1)) return;
    let others = list.filter((c) => c.el !== el);
    // Up or down while the page can still scroll that way: the page's own things
    // first, so the walk carries on past the fold instead of stopping on the
    // bug button pinned to the bottom of the screen.
    const dy = action === 'down' ? 1 : action === 'up' ? -1 : 0;
    const box = dy ? scroller() : null;
    if (dy && canScroll(box, dy)) others = others.filter((c) => !pinned(c.el));
    const i = pickNext(el.getBoundingClientRect(), others.map((c) => c.r), action);
    if (i >= 0) mark(others[i].el);
    else if (dy && canScroll(box, dy)) {
      box.scrollBy({ top: dy * Math.round(innerHeight * 0.4), behavior: 'instant' });
      // What scrolled into view, in that direction.
      const fresh = candidates().filter((c) => c.el !== el && !pinned(c.el));
      const j = pickNext(el.getBoundingClientRect(), fresh.map((c) => c.r), action);
      if (j >= 0) mark(fresh[j].el);
    }
  }
}

/**
 * Launched from the Steam Deck (its shortcuts open the game with ?deck=1).
 * The Deck draws at 1280x800 and a TV stretches that across the room, so
 * phone-sized type is too small to read from the couch. `zoom` scales the
 * whole page; the game lays itself out for a screen that much smaller, a size
 * it already handles. Remembered for the visit, so moving between screens
 * keeps it. Never applies without the flag: web and phone are untouched.
 */
export function isDeck () {
  try {
    if (new URLSearchParams(location.search).has('deck')) sessionStorage.setItem('atr:deck', '1');
    return sessionStorage.getItem('atr:deck') === '1';
  } catch { return new URLSearchParams(location.search).has('deck'); }
}

/**
 * Start listening for a controller. Safe to call once at boot. On the Deck it
 * also marks <html class="deck"> for any Deck-only styling. (The Deck's text
 * size for the TV comes from Chrome's own scale in the Steam launch line -
 * zooming the page from here cut off every full-height layout.)
 */
export function installPadNav () {
  if (typeof window !== 'undefined' && isDeck()) document.documentElement.classList.add('deck');
  if (typeof window === 'undefined' || !('getGamepads' in navigator) || window.__padNav) return;
  const repeater = createRepeater();
  let kind = null; let styled = false; let polling = false;
  const poll = () => {
    const pads = Array.from(navigator.getGamepads()).filter(Boolean);
    const now = pads.length ? padKind(pads[0].id) : null;
    if (now !== kind) {
      kind = now; repeater.reset();
      document.documentElement.classList.toggle('pad-nav', !!kind);
      hint(kind);
      if (!kind) mark(null);
    }
    // Unplugged: look again now and then, in case the connect event never comes.
    if (!pads.length) { polling = false; setTimeout(start, 1000); return; }
    if (kind && !styled) { styled = true; const s = document.createElement('style'); s.textContent = CSS; document.head.appendChild(s); }
    const held = new Set();
    pads.forEach((p) => padActions(p).forEach((a) => held.add(a)));
    if (!document.hidden) repeater.tick(held, performance.now()).forEach((a) => { try { act(a); } catch (e) { /* a stray press must never break the game */ } });
    requestAnimationFrame(poll);
  };
  const start = () => { if (!polling) { polling = true; requestAnimationFrame(poll); } };
  // Chrome only lists a pad after it's been touched; both events and a first look start the loop.
  window.addEventListener('gamepadconnected', start);
  window.addEventListener('gamepaddisconnected', () => requestAnimationFrame(poll));
  if (Array.from(navigator.getGamepads()).some(Boolean)) start();
  // A mouse or a finger takes over again: drop the highlight until the pad is used.
  window.addEventListener('pointerdown', (e) => { if (e.isTrusted) mark(null); }, true);
  window.__padNav = { candidates: () => candidates().map((c) => c.el), current: () => current };
}
